import classNames from "classnames";
import {isAfter, isBefore} from "date-fns";
import {Dispatch, ReactNode, SetStateAction} from "react";
import {FiPlus} from "react-icons/fi";
import {Link} from "react-router";

import Button from "@/components/button/Button";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {TwoLineShortDateTimeUser} from "@/components/datetime/TwoLineShortDateTimeUser.tsx";
import {Icon} from "@/components/icon/Icon";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {TableEmptyStateCta} from "@/components/table/subComponents/TableEmptyStateCta/TableEmptyStateCta.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec, Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {IncidentsListResponseValues} from "@/lib/queries/statuspages.ts";
import {Incident, IncidentTime} from "@/lib/types/statuspages.ts";
import {IncidentEndDate} from "@/pages/incidents/components/IncidentEndDate.tsx";
import {IncidentsTableActions} from "@/pages/incidents/components/IncidentsTableActions.tsx";
import {IncidentStatePill} from "@/pages/incidents/IncidentStatePill.tsx";
import {getStatusPagePreviewURL} from "@/pages/statuspages/utils/urls.ts";

import styles from "./IncidentsTable.module.scss";

type IncidentsTableProps = {
  data: IncidentsListResponseValues;
  sortColumn: string;
  sortDirection: SortingDirection;
  onSortingChange?: (sorting: Sorting) => void;
  enableSorting?: boolean;
  pagination: LimitAndOffset;
  setPagination: (pagination: LimitAndOffset) => void;
  dataTestId?: string;
  isFiltered?: boolean;
  rowSelection?: Record<string, boolean>;
  setRowSelection?: Dispatch<SetStateAction<Record<string, boolean>>>;
  actionBar?: ReactNode;
};

export const IncidentsTable = ({
  data,
  sortColumn,
  sortDirection,
  onSortingChange,
  enableSorting = true,
  pagination,
  setPagination,
  dataTestId,
  isFiltered,
  rowSelection,
  setRowSelection,
  actionBar,
}: IncidentsTableProps) => {
  const {user, brand} = useCurrentUserContext();

  const specs: ColumnSpec<Incident>[] = [
    {
      fieldName: "name",
      fieldTitle: "Details",
      enableSorting: enableSorting,
      meta: {
        columnClassName: "pe-3",
      },
      accessor: (incident: Incident) => {
        const incidentType = incident.incident_type === "INCIDENT" ? "incidents" : "maintenance";
        let incidentTime = IncidentTime.ACTIVE;
        if (incident.ends_at && isBefore(new Date(incident.ends_at), new Date())) {
          incidentTime = IncidentTime.RESOLVED;
        }
        if (isAfter(new Date(incident.starts_at), new Date()) && incidentType === "maintenance") {
          incidentTime = IncidentTime.UPCOMING;
        }
        const link = `tab=${incidentType}&incidents_tab=${incidentTime}&search=${incident.id}&incident_id=${incident.id}`;
        return (
          <Link
            className={"pulseTextBodySmall pulse-color-gray-900"}
            to={`/statuspages/${incident.statuspage.id}?${link}`}
          >
            <Tooltip tooltipContent={incident.name} delayMs={500}>
              <div className="ellipsis-overflow">{incident.name}</div>
            </Tooltip>
            <Tooltip tooltipContent={incident.latest_update_description} delayMs={500}>
              <div className="pulse-color-gray-600 ellipsis-overflow">{incident.latest_update_description}</div>
            </Tooltip>
          </Link>
        );
      },
    },
    {
      fieldName: "statuspage__name",
      fieldTitle: "Status Page",
      enableSorting: enableSorting,
      meta: {
        columnClassName: "pe-3",
      },
      accessor: (incident: Incident) => {
        if (incident.statuspage.public_url) {
          const defaultUrl = getStatusPagePreviewURL(brand, incident.statuspage.id, incident.statuspage.public_url);
          return (
            <a href={defaultUrl} target={"_blank"}>
              <Tooltip tooltipContent={incident.statuspage.name} delayMs={500}>
                <div className="ellipsis-overflow">{incident.statuspage.name}</div>
              </Tooltip>
            </a>
          );
        }
      },
    },
    {
      fieldName: "latest_update_incident_state",
      fieldTitle: "Update",
      enableSorting: enableSorting,
      meta: {
        headerClassName: classNames("text-nowrap", styles.updateColumn),
      },
      accessor: (incident: Incident) => (
        <IncidentStatePill state={incident.latest_update_incident_state} variant={"textAndIcon"} />
      ),
    },
    {
      fieldName: "starts_at",
      fieldTitle: "Start",
      enableSorting: enableSorting,
      meta: {
        headerClassName: classNames("text-nowrap", styles.dateColumn),
      },
      accessor: (incident: Incident) => <TwoLineShortDateTimeUser date={incident.starts_at.toString()} user={user!} />,
    },
    {
      fieldName: "end_compound",
      fieldTitle: "End",
      enableSorting: enableSorting,
      meta: {
        headerClassName: classNames("text-nowrap", styles.dateColumn),
      },
      accessor: (incident: Incident) => <IncidentEndDate endsAt={incident.ends_at} user={user!} />,
    },
    ...(user?.has_write_access
      ? [
          {
            fieldName: "actions",
            meta: {
              headerClassName: styles.actionColumn,
            },
            accessor: (incident: Incident) => <IncidentsTableActions incident={incident} />,
          },
        ]
      : []),
  ];

  return (
    <>
      <Table
        rows={data.data}
        specs={specs}
        initialState={{columnPinning: {right: ["actions"]}}}
        sorting={{column: sortColumn, direction: sortDirection}}
        onSortingChange={onSortingChange}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        getRowId={(incident) => incident.id.toString()}
        actionBar={actionBar}
        noDataComponent={
          <TableEmptyStateCta
            colSpan={specs.length + (setRowSelection ? 1 : 0)}
            title={
              isFiltered
                ? "No Incidents or Maintenance Events found matching the filter(s) above."
                : "You do not have any Incidents."
            }
            cta={
              isFiltered ? undefined : (
                <Button as={Link} to="/incidents/create" variant="secondary">
                  <Icon icon={FiPlus} />
                  <span>Create Incident</span>
                </Button>
              )
            }
          />
        }
        fixedWidth
        minWidth={"800px"}
        dataTestId={dataTestId}
        className={classNames(styles.incidentsTable, "rounded-top rounded-bottom")}
        pagination={pagination}
        updatePagination={setPagination}
        totalRows={data.total}
        paginationData={data.data}
      />
    </>
  );
};
